/* Internal footer pages use a native dialog. The COD app owns its own controls. */
(() => {
  if (window.sxFooterModalReady) return;
  window.sxFooterModalReady = true;

  function initialize(footer) {
    if (footer._sxModalCleanup) footer._sxModalCleanup();
    const dialog = footer.querySelector('[data-sx-policy-dialog]');
    const body = footer.querySelector('[data-sx-policy-body]');
    const title = footer.querySelector('[data-sx-policy-title]');
    const close = footer.querySelector('[data-sx-policy-close]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    const listeners = new AbortController();
    let request;
    let returnFocus;
    let revision = 0;

    function cancelRequest() {
      revision += 1;
      if (request) request.abort();
      request = null;
    }

    function sameOriginUrl(value) {
      try {
        const url = new URL(value, location.href);
        return /^https?:$/.test(url.protocol) && url.origin === location.origin ? url : null;
      } catch (_) {
        return null;
      }
    }

    footer.querySelectorAll('.sx-footer-links a[data-sx-footer-page]').forEach(link => {
      try {
        const url = new URL(link.getAttribute('href'), location.href);
        if (/^https?:$/.test(url.protocol) && url.origin !== location.origin) {
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
        }
      } catch (_) { /* Invalid links retain their normal browser behavior. */ }
    });

    function sanitize(fragment, baseUrl) {
      fragment.querySelectorAll('script, style, link, base, iframe, object, embed, form, dialog').forEach(node => node.remove());
      fragment.querySelectorAll('*').forEach(node => {
        Array.from(node.attributes).forEach(attribute => {
          if (/^on/i.test(attribute.name) || attribute.name === 'srcdoc') node.removeAttribute(attribute.name);
        });
        for (const attribute of ['href', 'src', 'action', 'xlink:href']) {
          const value = node.getAttribute(attribute);
          if (!value) continue;
          try {
            const url = new URL(value, baseUrl);
            if (!['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol)) node.removeAttribute(attribute);
            else node.setAttribute(attribute, url.href);
          } catch (_) {
            node.removeAttribute(attribute);
          }
        }
        if (node.tagName === 'A' && node.target === '_blank') node.rel = 'noopener noreferrer';
      });
      return fragment;
    }

    function showContent(fragment, baseUrl) {
      body.replaceChildren(sanitize(fragment, baseUrl));
      body.removeAttribute('aria-busy');
      body.scrollTop = 0;
    }

    footer.addEventListener('click', async event => {
      const link = event.target.closest('.sx-footer-links a[data-sx-footer-page]');
      if (!link || !footer.contains(link) || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const url = sameOriginUrl(link.getAttribute('href'));
      if (!url || url.hash && url.pathname === location.pathname && url.search === location.search) return;
      event.preventDefault();
      cancelRequest();
      const currentRevision = revision;
      returnFocus = link;
      title.textContent = link.textContent.trim() || 'Informații utile';
      body.textContent = 'Se încarcă…';
      body.setAttribute('aria-busy', 'true');
      if (!dialog.open) dialog.showModal();
      document.body.classList.add('sx-policy-open');
      close.focus({ preventScroll: true });

      // Shopify policies are already in the HTML: instant opening, no page request.
      const policy = Array.from(footer.querySelectorAll('template[data-sx-policy-url]')).find(template => {
        const policyUrl = template.dataset.sxPolicyUrl && sameOriginUrl(template.dataset.sxPolicyUrl);
        return policyUrl && policyUrl.pathname.replace(/\/$/, '') === url.pathname.replace(/\/$/, '') && template.content.textContent.trim();
      });
      if (policy) {
        showContent(policy.content.cloneNode(true), url.href);
        return;
      }

      // Other selected store pages are fetched from this store and only their content is displayed.
      const controller = new AbortController();
      request = controller;
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url.href, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok || !sameOriginUrl(response.url) || !response.headers.get('content-type')?.includes('text/html')) throw new Error('Page unavailable');
        const documentPage = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = documentPage.querySelector('.shopify-policy__body, .sx-prose, .rte, [data-page-content]');
        if (!content || !content.textContent.trim()) throw new Error('Page content unavailable');
        if (currentRevision !== revision || !dialog.open) return;
        const fragment = document.createDocumentFragment();
        Array.from(content.childNodes).forEach(node => fragment.appendChild(node.cloneNode(true)));
        showContent(fragment, response.url);
      } catch (_) {
        if (currentRevision !== revision || !dialog.open) return;
        const message = document.createElement('p');
        message.textContent = 'Conținutul nu s-a putut încărca. Poți închide fereastra sau deschide pagina.';
        const fallback = document.createElement('a');
        fallback.href = url.href;
        fallback.textContent = 'Deschide pagina';
        body.replaceChildren(message, fallback);
        body.removeAttribute('aria-busy');
      } finally {
        clearTimeout(timeout);
        if (request === controller) request = null;
      }
    }, { signal: listeners.signal });

    close.addEventListener('click', () => dialog.close(), { signal: listeners.signal });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    }, { signal: listeners.signal });
    dialog.addEventListener('close', () => {
      if (dialog.open) return;
      cancelRequest();
      document.body.classList.remove('sx-policy-open');
      body.removeAttribute('aria-busy');
      if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
    }, { signal: listeners.signal });

    footer._sxModalCleanup = () => {
      cancelRequest();
      if (dialog.open) dialog.close();
      listeners.abort();
      document.body.classList.remove('sx-policy-open');
      delete footer._sxModalCleanup;
    };
  }

  function initializeAll(scope) {
    if (scope.matches?.('[data-sx-footer]')) initialize(scope);
    scope.querySelectorAll('[data-sx-footer]').forEach(initialize);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll(document), { once: true });
  else initializeAll(document);
  document.addEventListener('shopify:section:load', event => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.matches?.('[data-sx-footer]')) event.target._sxModalCleanup?.();
    event.target.querySelectorAll('[data-sx-footer]').forEach(footer => footer._sxModalCleanup?.());
  });
})();
